import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { ChatMode } from "../components/chat/helpers.js";

export interface OpenChatOptions {
  /** Prompt to send automatically in a fresh conversation. Defaults `mode` to "chat" when omitted. */
  prompt?: string;
  /** Which chat type to start a fresh conversation in. */
  mode?: ChatMode;
}

interface ChatContextValue {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  /** With no options, just opens the widget as-is. With options, starts a new conversation. */
  openChat: (options?: OpenChatOptions) => void;
  /** Pending request from `openChat` that the chat widget has yet to apply. */
  pendingRequest: (OpenChatOptions & { id: number }) | null;
  clearPendingRequest: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<ChatContextValue["pendingRequest"]>(null);
  const requestIdRef = useRef(0);

  const openChat = useCallback((options?: OpenChatOptions) => {
    setIsOpen(true);
    if (options?.prompt || options?.mode) {
      requestIdRef.current += 1;
      setPendingRequest({ ...options, id: requestIdRef.current });
    }
  }, []);

  const clearPendingRequest = useCallback(() => setPendingRequest(null), []);

  const value = useMemo<ChatContextValue>(
    () => ({ isOpen, setIsOpen, openChat, pendingRequest, clearPendingRequest }),
    [isOpen, openChat, pendingRequest, clearPendingRequest],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export function useChat(): ChatContextValue {
  const context = useContext(ChatContext);
  if (!context) throw new Error("useChat must be used within a ChatProvider");
  return context;
}
